/**
 * 뉴스레터 백오피스 API
 *
 * 서버: NewsletterUiController (/api/ui/newsletters)
 */

import { apiClient } from './apiClient';

// ---------------------------------------------------------------------------
// 타입
// ---------------------------------------------------------------------------

export interface AdminNewsletter {
  newsletter_id: number;
  title: string;
  author: string;
  tags: string | null;
  badges: string[];
  thumbnail_url: string | null;
  external_url: string | null;
  is_temp: boolean;
  published_at: string;
  created_at: string;
}

export interface AdminNewsletterPage {
  content: AdminNewsletter[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
}

export interface AdminNewsletterBanner {
  banner_id: number;
  caption: string | null;
  title: string | null;
  font_color_hex_code: string | null;
  banner_image_url: string | null;
}

export interface AdminNewsletterFeatured {
  featured_id: number | null;
  caption: string | null;
  text: string | null;
  featured_image_url: string | null;
  card_title: string | null;
  card_caption: string | null;
  /** 카드에 연결된 뉴스레터 (연결이 없거나 뉴스레터가 삭제됐으면 null) */
  newsletter_id: number | null;
  newsletter_title: string | null;
  newsletter_external_url: string | null;
}

export interface NewsletterPayload {
  title: string;
  author: string;
  tags: string;
  externalUrl: string;
  isTemp: boolean;
  thumbnailImage?: File | null;
}

export interface NewsletterBannerPayload {
  caption: string;
  title: string;
  fontColorHexCode: string;
  bannerImage?: File | null;
}

export interface NewsletterFeaturedPayload {
  caption: string;
  text: string;
  cardTitle: string;
  cardCaption: string;
  /** 카드에 연결할 뉴스레터. null 이면 연결을 해제한다. */
  newsletterId: number | null;
  featuredImage?: File | null;
  /** true 면 서버에 저장된 기존 이미지를 지운다. featuredImage 가 함께 있으면 교체가 우선한다. */
  removeImage?: boolean;
}

const BASE = '/api/ui/newsletters';

// 이미지 업로드가 있는 요청은 multipart 로 보낸다. (boundary 는 브라우저가 채운다)
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

// ---------------------------------------------------------------------------
// 뉴스레터
// ---------------------------------------------------------------------------

// 백오피스 목록 조회 (임시 저장 + 등록 완료). search 는 제목·작성자 부분 일치
export const getAdminNewsletters = async (
  page = 0,
  size = 10,
  isTemp?: boolean,
  search?: string
): Promise<AdminNewsletterPage> => {
  try {
    const params: Record<string, any> = { page, size };
    if (isTemp !== undefined) params.isTemp = isTemp;
    if (search && search.trim()) params.search = search.trim();

    const response = await apiClient.get(`${BASE}/admin/list`, { params });
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 목록을 불러올 수 없습니다.');
  }
};

// 단건 조회 (수정 화면 진입용)
export const getAdminNewsletter = async (newsletterId: number): Promise<AdminNewsletter> => {
  try {
    const response = await apiClient.get(`${BASE}/${newsletterId}`);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터를 불러올 수 없습니다.');
  }
};

const toNewsletterFormData = (payload: NewsletterPayload): FormData => {
  const formData = new FormData();
  formData.append('title', payload.title);
  formData.append('author', payload.author);
  formData.append('tags', payload.tags);
  formData.append('externalUrl', payload.externalUrl);
  formData.append('isTemp', String(payload.isTemp));
  if (payload.thumbnailImage) {
    formData.append('thumbnailImage', payload.thumbnailImage);
  }
  return formData;
};

// 등록
export const createNewsletter = async (payload: NewsletterPayload): Promise<AdminNewsletter> => {
  try {
    const response = await apiClient.post(BASE, toNewsletterFormData(payload), MULTIPART);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 등록에 실패했습니다.');
  }
};

// 수정
export const updateNewsletter = async (
  newsletterId: number,
  payload: NewsletterPayload
): Promise<AdminNewsletter> => {
  try {
    const response = await apiClient.put(`${BASE}/${newsletterId}`, toNewsletterFormData(payload), MULTIPART);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 수정에 실패했습니다.');
  }
};

// 삭제
export const deleteNewsletter = async (newsletterId: number): Promise<void> => {
  try {
    await apiClient.delete(`${BASE}/${newsletterId}`);
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 삭제에 실패했습니다.');
  }
};

// ---------------------------------------------------------------------------
// 배너
// ---------------------------------------------------------------------------

export const getNewsletterBanners = async (): Promise<AdminNewsletterBanner[]> => {
  try {
    const response = await apiClient.get(`${BASE}/banners/list`);
    return response.data?.data ?? [];
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 배너를 불러올 수 없습니다.');
  }
};

const toBannerFormData = (payload: NewsletterBannerPayload): FormData => {
  const formData = new FormData();
  formData.append('caption', payload.caption);
  formData.append('title', payload.title);
  formData.append('fontColorHexCode', payload.fontColorHexCode);
  if (payload.bannerImage) {
    formData.append('bannerImage', payload.bannerImage);
  }
  return formData;
};

export const createNewsletterBanner = async (
  payload: NewsletterBannerPayload
): Promise<AdminNewsletterBanner> => {
  try {
    const response = await apiClient.post(`${BASE}/banners`, toBannerFormData(payload), MULTIPART);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 배너 등록에 실패했습니다.');
  }
};

export const updateNewsletterBanner = async (
  bannerId: number,
  payload: NewsletterBannerPayload
): Promise<AdminNewsletterBanner> => {
  try {
    const response = await apiClient.put(`${BASE}/banners/${bannerId}`, toBannerFormData(payload), MULTIPART);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 배너 수정에 실패했습니다.');
  }
};

export const deleteNewsletterBanner = async (bannerId: number): Promise<void> => {
  try {
    await apiClient.delete(`${BASE}/banners/${bannerId}`);
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 배너 삭제에 실패했습니다.');
  }
};

// ---------------------------------------------------------------------------
// 강조 섹션
// ---------------------------------------------------------------------------

export const getNewsletterFeatured = async (): Promise<AdminNewsletterFeatured> => {
  try {
    const response = await apiClient.get(`${BASE}/featured`);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 강조 섹션을 불러올 수 없습니다.');
  }
};

export const saveNewsletterFeatured = async (
  payload: NewsletterFeaturedPayload
): Promise<AdminNewsletterFeatured> => {
  try {
    const formData = new FormData();
    formData.append('caption', payload.caption);
    formData.append('text', payload.text);
    formData.append('cardTitle', payload.cardTitle);
    formData.append('cardCaption', payload.cardCaption);
    // 빈 문자열은 서버에서 null 로 바인딩되어 연결이 해제된다.
    formData.append('newsletterId', payload.newsletterId === null ? '' : String(payload.newsletterId));
    if (payload.featuredImage) {
      formData.append('featuredImage', payload.featuredImage);
    } else if (payload.removeImage) {
      formData.append('removeImage', 'true');
    }

    const response = await apiClient.put(`${BASE}/featured`, formData, MULTIPART);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '뉴스레터 강조 섹션 저장에 실패했습니다.');
  }
};
